<script>
	import { render_micro_markdown } from '#lib/util.js';
	import FormInputWithLabel from '#lib/FormInputWithLabel.svelte';
  import t from '#lib/i18n.svelte.js';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import MainPageContent from '#lib/MainPageContent.svelte';
  import CustomFieldsForm from '#lib/CustomFieldsForm.svelte';
  import { getContext } from 'svelte';
  import Alert from '#lib/Alert.svelte';

  let { form, data } = $props();
  
  let site_settings = getContext('site_settings');
  
  let custom_field_values = $state(data?.custom_fields || {});
  let return_to_current = $derived(form?.return_to ?? data?.return_to ?? null);
  let login_root_href = $derived(return_to_current ? `/login?return_to=${encodeURIComponent(return_to_current)}` : '/login');
  let email_value = $state(data?.email || '');
  let name_value = $state(form?.name ?? data?.name ?? '');
  let consent_given = $state(data?.consent_given || false);
</script>

<svelte:head>
  <title>{t('sign_up_or_log_in')}</title>
</svelte:head>

{#snippet login_form_consent()}
  {#if site_settings.privacy_policy_preview_ref || site_settings.terms_and_conditions_preview_ref}
    <div class='flex-row gap-x g-3 items-center'>
      {#if site_settings.signup_data_processing_consent_required}
        <input type='checkbox' name='consent_to_privacy_policy' value='1' bind:checked={consent_given} required={true} />
      {/if}
      <p class='text-caption'>
        {#if site_settings.privacy_policy_preview_ref && site_settings.terms_and_conditions_preview_ref}
          {@html t('by_signing_up_you_agree_to_the_privacy_policy_and_terms_and_conditions', {
            privacy_policy: site_settings.privacy_policy_preview_ref,
            terms_and_conditions: site_settings.terms_and_conditions_preview_ref
          })}
        {:else if site_settings.privacy_policy_preview_ref}
          {@html t('by_signing_up_you_agree_to_the_privacy_policy', {
            privacy_policy: site_settings.privacy_policy_preview_ref
          })}
        {:else if site_settings.terms_and_conditions_preview_ref}
          {@html t('by_signing_up_you_agree_to_the_terms_and_conditions', {
            terms_and_conditions: site_settings.terms_and_conditions_preview_ref
          })}
      {/if}
      </p>
      {#if form?.error_field === 'consent_to_privacy_policy'}
        <Alert variant='error' dismissible={true}>{form.error_message}</Alert>
      {/if}
    </div>
  {/if}
{/snippet}

{#snippet code_sent_notice(key, email, vars = {})}
  {@const parts = t(key, { ...vars, email: '\u0000' }).split('\u0000')}
  <p class='text-body'>{parts[0]}<span class='email-mark bg-2'>{email}</span>{parts[1] ?? ''}</p>
{/snippet}

<!-- The Back button of every signup stage. It submits the hidden reset form below
     through its form= attribute, so inside the details and code forms it is not
     their default button: Enter in a field still submits the stage itself. -->
{#snippet reset_signup_button(label)}
  <button type='submit' form='reset-signup-form'>
    <ButtonLabel variant='secondary {site_settings.secondary_control_surface}'>{label}</ButtonLabel>
  </button>
{/snippet}

<MainPageContent>
  <BlockGrid>
    <div class='log-in-sign-in-form gap-y g-8 flex-column justify-center'>
      <!-- A form, not a link: the router preloads links on hover, and forgetting
           the pending signup must only ever happen on a click. -->
      <form id='reset-signup-form' method='POST' action='?/reset_signup' hidden>
        <input type='hidden' name='return_to' value={return_to_current || ''} />
      </form>
      {#if form?.stage === 'login_code_incorrect'}  
    
        <h1 class='text-heading2'>{t('login_code_incorrect')}</h1>
        <a href={login_root_href}>
          <ButtonLabel variant='secondary {site_settings.secondary_control_surface}'>{t('go_back')}</ButtonLabel>      
        </a>
    
      {:else if form?.stage === 'signup_code_incorrect'}

        <h1 class='text-heading2'>{t('signup_code_incorrect')}</h1>
        {@render reset_signup_button(t('go_back'))}

      {:else if form?.stage === 'blocked'}

        <h1 class='text-heading2'>{t('account_blocked_title')}</h1>
        <p class='text-body text-2'>
          {site_settings.support_email
            ? t('account_blocked_message_with_support', { email: site_settings.support_email })
            : t('account_blocked_message')}
        </p>
        <a href={login_root_href}>
          <ButtonLabel variant='secondary {site_settings.secondary_control_surface}'>{t('go_back')}</ButtonLabel>
        </a>

      {:else if form?.stage === 'prompt_signup_details' || (!form?.stage && data?.signup_stage === 'prompt_signup_details')}
        <form method='POST' action='?/save_signup_details' class='flex-column gap-y g-5'>
          <h1 class='text-heading2'>{t('sign_up')}</h1>
          <FormInputWithLabel 
            type='text' 
            label='name' 
            bind:value={name_value}
            placeholder={t('enter_your_full_name')} 
            orientation='column' 
            variant='canvas outlined'
            pattern={".*\\S.*"}
            required={true}
            autocomplete='name'
          />
          {#if form?.error_field === 'name'}
            <Alert variant='error' dismissible={true}>{form.error_message}</Alert>
          {/if}
        
          <!-- Custom form fields -->
          <CustomFieldsForm 
            fields={site_settings.custom_form_fields || []}
            bind:values={custom_field_values}
            editable={false}
            errors={form?.error_field ? { [form.error_field]: form.error_message } : {}}
          />
        
          <input type='hidden' name='return_to' value={return_to_current || ''} />

          {@render login_form_consent()}
        
          <div class='flex g-4 gap-x stretch-children'>
            {@render reset_signup_button(t('back'))}
            <button type='submit'>
              <ButtonLabel variant='primary {site_settings.primary_control_surface}'>{t('continue_to_verification')}</ButtonLabel>
            </button>
          </div>
        </form>
    
    
      {:else if form?.stage === 'confirm_signup_code'}  
    
        <form method='POST' action='?/confirm_signup_code' class='flex-column gap-y g-5'>
          <h1 class='text-heading2'>{t('sign_up')}</h1>
          {@render code_sent_notice('signup_code_sent', form.email)}
          <FormInputWithLabel 
            type='text'
            label='signup_code'
            placeholder={t('paste_signup_code')}
            required={true}
            orientation='column' 
            variant='canvas outlined'
            autocomplete='one-time-code'
            autofocus={true}
            inputmode='numeric'
          />
          <input type='hidden' name='return_to' value={form.return_to} />
          <div class='flex g-3 gap-x stretch-children'>
            {@render reset_signup_button(t('back'))}
            <button type='submit'>
              <ButtonLabel variant='primary {site_settings.primary_control_surface}'>{t('continue')}</ButtonLabel>
            </button>
          </div>
          <div class='text-caption text-3'>{site_settings.support_email ? t('code_not_received_get_assistance',{email: site_settings.support_email}) : t('code_not_received')}</div>
        </form>
    
    
      {:else if form?.stage === 'confirm_login_code'}  
    
        <form method='POST' action='?/confirm_login_code' class='flex-column gap-y g-5'>
          <h1 class='text-heading2'>{t('check_your_email')}</h1>
          {@render code_sent_notice('login_code_sent', form.user_email, { name: ((form?.user_name || form?.user_email || '')).split(' ')[0] })}
          <FormInputWithLabel 
            type='text' 
            label='enter_code' 
            required={true}
            placeholder={t('enter_code')} 
            orientation='column' 
            variant='canvas outlined'
            name='login_code'
            autocomplete='one-time-code'
            autofocus={true}
            inputmode='numeric'
          />
          <input type='hidden' name='return_to' value={form.return_to} />
          <div class='flex g-3 gap-x stretch-children'>
            <a href={login_root_href}>
              <ButtonLabel variant='secondary {site_settings.secondary_control_surface}'>{t('back')}</ButtonLabel>      
            </a>
            <button type='submit'>
              <ButtonLabel variant='primary {site_settings.primary_control_surface}'>{t('continue')}</ButtonLabel>
            </button>
          </div>
          <div class='text-caption text-3'>{site_settings.support_email ? t('code_not_received_get_assistance',{email: site_settings.support_email}) : t('code_not_received')}</div>
        </form>
      {:else}
        <div class='flex-column gap-y g-5'>
          <h1 class='text-heading2'>{t('sign_up_or_log_in')}</h1>
          <p class='text-body text-2'>{@html render_micro_markdown(site_settings.signup_or_login_text)}</p>
        </div>
        <form method='POST' action='?/request_confirmation_code' class='flex-column gap-y g-5'>
          {#if form?.rate_limited}
            <Alert variant='error' dismissible={true}>{t('rate_limited_message')}</Alert>
          {:else if form?.invalid_email}
            <Alert variant='error' dismissible={true}>{t('email_invalid')}</Alert>
          {/if}
          <FormInputWithLabel 
            type='email' 
            label='email' 
            bind:value={email_value}
            placeholder={t('enter_your_email')} 
            orientation='column' 
            variant='canvas outlined'
            required={true} 
            autocomplete='email'
          />
          <input type='hidden' name='return_to' value={return_to_current || ''} />
          <input type='hidden' name='name' value={name_value} />

          <button type='submit'>
            <ButtonLabel variant='primary {site_settings.primary_control_surface}'>{t('request_login_or_signup_code')}</ButtonLabel>
          </button>

        </form>
      {/if}
    </div>
  </BlockGrid>
</MainPageContent>

<style lang='postcss'>
  /* The address the code went to, marked in the sentence. Same step the
     design system takes for a quieter surface: darker on a light canvas,
     brighter on a dark one, via the lux-mode sign in .bg-2 */
  .email-mark {
    padding-inline: var(--size-2);
    border-radius: var(--radius-xs, var(--size-1));
    /* The body copy hyphenates, which would invent a "-" inside the address
       ("…ex-ample"); it may wrap, but only between its own characters */
    hyphens: none;
    overflow-wrap: break-word;
    /* The padding follows the address when the sentence wraps mid-address */
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  .log-in-sign-in-form {
    min-height: calc(100svh - var(--dynamic-island-bottom-edge));
    width: 100%;
    grid-column: 10 / 41;
    max-width: 400px;
    margin: 0 auto;
    padding-block-start: var(--size-8);
    padding-block-end: 15vh;
    @media (min-width: 640px) {
      grid-column: 20 / 31;
    }
  }
</style>

